// M-74 (promo): 商材別 engine 設定（docs/INTEREST-ENGINE-DESIGN.md §5 商材プロファイル）。
// 既定テンプレートと「明示上書き → 商材別既定」の 2 層解決純関数を持つ。
// promoInterest.ts（ステージ判定の実体）から分離した新設ファイルで、import は
// promoEngine → promoInterest の一方向（循環なし）。SDK 非依存（fuelPriceDerive.ts と同じ方針）。
//
// 差分保存の原則（M-81 の全量焼き込みの轍を踏まない）:
//   - `InterestTagConfig.engines` には明示設定した値のみ保存される（UI が既定値を送らない）
//   - 未設定キー / フィールドは評価時に必ず本ファイルの既定へフォールバックする
//   - よって既定テンプレートの将来変更（TP-12 パイロットでの調整）は未上書きテナントへ自動追従する
import { DEFAULT_STAGE_PROMOTION_RULES, applyStageDecay } from './promoInterest.js';
/**
 * hot の閾値パラメータの普遍既定（M-76 / 仮置き・TP-12 パイロット実データで検証）。
 * `enabled` だけが商材ごとに異なり、閾値は全商材共通の初期値を持つ
 * （§5 の商材別パラメータ表に閾値の列がないため。テナント上書きは商材ごとに可能）。
 * - 急増 = 「7 日以内に 4 日接触」（能動接触の同一 JST 暦日は 1 と数える）
 * - 自動フォロー = 「対応中のまま 3 日無対応」（§7 の既定値）
 * - 再起票冷却 = クローズから 30 日
 */
export const HOT_PARAM_DEFAULTS = {
    surgeWindowDays: 7,
    surgeEventCount: 4,
    followUpAfterDays: 3,
    reopenCooldownDays: 30,
};
/** 商材キー → 既定エンジンタイプ（§5 / WashPass は②で確定 2026-07-14）。 */
export const DEFAULT_ENGINE_TYPE_BY_KEY = {
    inspection: 'periodic',
    keeper: 'periodic',
    car_sales: 'considered',
    washpass: 'considered',
    fuel: 'frequent',
};
/**
 * 商材別 engine 既定値（§5 パラメータ表が真実源 / 仮置き・TP-12 パイロット実データで検証）。
 * - 促進（cumulative）は M-71 で先行導入したコード定数（DEFAULT_STAGE_PROMOTION_RULES.
 *   toDesireCumulative）と同値 — 乖離は promoEngine.test.ts の同期テストで検知する
 * - decay は §5 の商材別値（M-72 の旧グローバル D60/I180 からの**意図的な挙動変更** /
 *   CHANGELOG 記載）。`desireDays: null` = タイプ②の「減衰せず追跡」。fuel の desireDays は
 *   §5 表で「—」のため現行値 60 を据え置く（fuel は cumulative null + coupon gate で
 *   desire 到達経路が booking_page_reach のみ = 実質未使用の安全側既定）
 * - couponClaimToDesire は §5「クーポン取得でD直行」列（燃料のみ ×）
 * - cycle / hot は M-74 では保存・表示のみ（cycle の消費は M-75 / hot は M-76）
 * - churn は M-75（タイプ③の離反検知）。既定は燃料のみ有効 — 「通常間隔の 2 倍空いたら
 *   逸脱・直近 6 間隔の中央値・最少 3 間隔」（仮置き / TP-12 パイロット実データで検証）。
 *   現状は給油の来店イベントが商材解決不能（M-80 待ち）のため実発火なし（fail-closed）
 */
export const DEFAULT_INTEREST_ENGINES = {
    inspection: {
        type: 'periodic',
        cumulative: { windowDays: 21, touchCount: 2 },
        couponClaimToDesire: true,
        decay: { desireDays: 90, interestDays: 365 },
        cycle: { kind: 'expiry', months: 22, expirySourced: true, leadMonths: 3 },
        hot: { enabled: false, ...HOT_PARAM_DEFAULTS },
        churn: null,
    },
    car_sales: {
        type: 'considered',
        cumulative: { windowDays: 30, touchCount: 2 },
        couponClaimToDesire: true,
        decay: { desireDays: null, interestDays: 365 },
        cycle: null,
        hot: { enabled: true, ...HOT_PARAM_DEFAULTS },
        churn: null,
    },
    keeper: {
        type: 'periodic',
        cumulative: { windowDays: 14, touchCount: 2 },
        couponClaimToDesire: true,
        decay: { desireDays: 45, interestDays: 180 },
        cycle: { kind: 'interval', months: 12, expirySourced: false, leadMonths: 0 },
        hot: { enabled: false, ...HOT_PARAM_DEFAULTS },
        churn: null,
    },
    washpass: {
        type: 'considered',
        cumulative: { windowDays: 14, touchCount: 2 },
        couponClaimToDesire: true,
        decay: { desireDays: null, interestDays: 180 },
        cycle: null,
        hot: { enabled: true, ...HOT_PARAM_DEFAULTS },
        churn: null,
    },
    fuel: {
        type: 'frequent',
        cumulative: null,
        couponClaimToDesire: false,
        decay: { desireDays: 60, interestDays: 60 },
        cycle: null,
        hot: { enabled: false, ...HOT_PARAM_DEFAULTS },
        churn: { enabled: true, multiplier: 2, minIntervals: 3, maxIntervals: 6 },
    },
};
/** engine 設定を持つ全商材キー（表示順 = INTEREST_KEY_OPTIONS と同じ）。 */
const ENGINE_KEYS = [
    'car_sales',
    'inspection',
    'keeper',
    'washpass',
    'fuel',
];
function templateOf(key) {
    const e = DEFAULT_INTEREST_ENGINES[key];
    return {
        cumulative: e.cumulative,
        couponClaimToDesire: e.couponClaimToDesire,
        decay: e.decay,
        cycle: e.cycle,
        hot: e.hot,
        churn: e.churn,
    };
}
/**
 * タイプ → 代表テンプレート（UI の「タイプ選択 → テンプレ自動適用」でフォームへ展開する
 * 専用 / 将来 M-77 のカスタム商材初期値）。実行時解決には使わない — 解決は常に
 * 「明示上書き → 商材別既定」の 2 層で、type を変えただけで数値が暗黙に変わることはない
 * （テンプレ適用で明示値化された値だけが保存される）。
 * 代表列: ①周期実施 = keeper / ②長期検討 = car_sales / ③高頻度継続 = fuel（仮置き）。
 */
export const ENGINE_TYPE_TEMPLATES = {
    periodic: templateOf('keeper'),
    considered: templateOf('car_sales'),
    frequent: templateOf('fuel'),
};
/**
 * 累積昇格ルールの解決。
 * - `cumulativeDisabled: true` は商材によらず null（累積昇格しない）— 既定ありの商材を
 *   対象外へ落とす唯一の明示表現（タイプ③テンプレ適用・UI の「累積昇格しない」）
 * - 既定が `null`（対象外）の商材は windowDays + touchCount の**両方**が明示されたとき
 *   のみ有効化する（片方だけでは規則を構成できないため null 維持 = fail-closed）
 * - 既定ありの商材はフィールド単位マージ
 */
function resolveCumulative(base, ov) {
    if (ov?.cumulativeDisabled === true)
        return null;
    if (base === null) {
        if (ov?.windowDays !== undefined && ov?.touchCount !== undefined) {
            return { windowDays: ov.windowDays, touchCount: ov.touchCount };
        }
        return null;
    }
    if (!ov)
        return { ...base };
    return {
        windowDays: ov.windowDays !== undefined ? ov.windowDays : base.windowDays,
        touchCount: ov.touchCount !== undefined ? ov.touchCount : base.touchCount,
    };
}
/**
 * cycle の解決。既定 `null`（周期なし）の商材は kind + months の両方が明示されたときのみ
 * 有効化する（欠落フィールドは expirySourced=false / leadMonths=0 で補完）。
 */
function resolveCycle(base, ov) {
    if (base === null) {
        if (ov?.kind !== undefined && ov?.months !== undefined) {
            return {
                kind: ov.kind,
                months: ov.months,
                expirySourced: ov.expirySourced ?? false,
                leadMonths: ov.leadMonths ?? 0,
            };
        }
        return null;
    }
    if (!ov)
        return { ...base };
    return {
        kind: ov.kind !== undefined ? ov.kind : base.kind,
        months: ov.months !== undefined ? ov.months : base.months,
        expirySourced: ov.expirySourced !== undefined ? ov.expirySourced : base.expirySourced,
        leadMonths: ov.leadMonths !== undefined ? ov.leadMonths : base.leadMonths,
    };
}
/**
 * hot の解決（M-76）。`cycle` / `churn` と違い base が常に非 null なので、素直な
 * フィールド単位マージで足りる（`enabled: false` でも閾値は解決され、UI の表示と
 * 「有効化した瞬間の挙動」が一致する）。閾値の下限は 1 に clamp する — 0 や負値は
 * 「窓なし = 全員急増」「猶予 0 日 = 起票直後に自動フォロー」という暴発を招くため
 * （設定不整合の fail-safe / `resolveChurn` の maxIntervals clamp と同方針）。
 */
function resolveHot(base, ov) {
    const atLeastOne = (v, fallback) => v !== undefined && Number.isFinite(v) ? Math.max(1, Math.floor(v)) : fallback;
    return {
        enabled: ov?.enabled !== undefined ? ov.enabled : base.enabled,
        surgeWindowDays: atLeastOne(ov?.surgeWindowDays, base.surgeWindowDays),
        surgeEventCount: atLeastOne(ov?.surgeEventCount, base.surgeEventCount),
        followUpAfterDays: atLeastOne(ov?.followUpAfterDays, base.followUpAfterDays),
        // 冷却日数だけは 0（冷却なし = 即再起票可）を意味値として通す。
        reopenCooldownDays: ov?.reopenCooldownDays !== undefined && Number.isFinite(ov.reopenCooldownDays)
            ? Math.max(0, Math.floor(ov.reopenCooldownDays))
            : base.reopenCooldownDays,
    };
}
/** churn パラメータの普遍既定（既定 null 商材を enabled 単独で有効化する際の補完値）。 */
const CHURN_PARAM_DEFAULTS = {
    multiplier: 2,
    minIntervals: 3,
    maxIntervals: 6,
};
/**
 * churn の解決（M-75）。cycle と異なり全パラメータに普遍既定があるため、既定 `null`
 * （非 frequent）の商材も `enabled: true` の明示だけで有効化できる（欠落パラメータは
 * `CHURN_PARAM_DEFAULTS` で補完）。`enabled: false` の明示は null（検知なし）。
 * `maxIntervals` は `minIntervals` 未満にならないよう clamp する（設定不整合の fail-safe）。
 */
function resolveChurn(base, ov) {
    if (ov?.enabled === false)
        return null;
    if (base === null && ov?.enabled !== true)
        return null;
    const resolved = {
        enabled: true,
        multiplier: ov?.multiplier ?? base?.multiplier ?? CHURN_PARAM_DEFAULTS.multiplier,
        minIntervals: ov?.minIntervals ?? base?.minIntervals ?? CHURN_PARAM_DEFAULTS.minIntervals,
        maxIntervals: ov?.maxIntervals ?? base?.maxIntervals ?? CHURN_PARAM_DEFAULTS.maxIntervals,
    };
    return {
        ...resolved,
        maxIntervals: Math.max(resolved.minIntervals, resolved.maxIntervals),
    };
}
/**
 * 商材 1 キー分の実効 engine 設定を解決する（純関数 / 非破壊）。
 * 解決は「config.engines[key] の明示値 → DEFAULT_INTEREST_ENGINES[key]」のフィールド単位
 * 2 層マージ。decay の `null` は「減衰せず追跡」の**意味を持つ明示値**として通し、
 * フィールド欠落（undefined）のみ既定へフォールバックする。
 */
export function resolveInterestEngine(key, config) {
    const base = DEFAULT_INTEREST_ENGINES[key];
    const ov = config?.engines?.[key];
    if (!ov)
        return base;
    return {
        type: ov.type !== undefined ? ov.type : base.type,
        cumulative: resolveCumulative(base.cumulative, ov.promotion),
        couponClaimToDesire: ov.promotion?.couponClaimToDesire !== undefined
            ? ov.promotion.couponClaimToDesire
            : base.couponClaimToDesire,
        decay: {
            desireDays: ov.decay?.desireDays !== undefined ? ov.decay.desireDays : base.decay.desireDays,
            interestDays: ov.decay?.interestDays !== undefined
                ? ov.decay.interestDays
                : base.decay.interestDays,
        },
        cycle: resolveCycle(base.cycle, ov.cycle),
        hot: resolveHot(base.hot, ov.hot),
        churn: resolveChurn(base.churn, ov.churn),
    };
}
/**
 * 商材 1 キー分の実効減衰ルール（`applyStageDecay` / `matchesSegment` 注入用の射影）。
 */
export function resolveStageDecayRule(key, config) {
    return resolveInterestEngine(key, config).decay;
}
/**
 * config から全商材分の `StagePromotionRules` を組み立てる（`deriveInterestStageDetails`
 * 等への注入用）。toInterest / toDesire / toAction は M-74 では config 化対象外のため
 * 既定（DEFAULT_STAGE_PROMOTION_RULES）を透過し、toDesireCumulative /
 * couponClaimToDesire を商材別に解決する。config 未指定時の結果は既定と実効同値
 * （couponClaimToDesire 全 true = gate 省略と等価 / promoEngine.test.ts で固定）。
 */
export function buildStagePromotionRules(config) {
    const toDesireCumulative = {};
    const couponClaimToDesire = {};
    for (const key of ENGINE_KEYS) {
        const engine = resolveInterestEngine(key, config);
        toDesireCumulative[key] = engine.cumulative;
        couponClaimToDesire[key] = engine.couponClaimToDesire;
    }
    return {
        toInterest: DEFAULT_STAGE_PROMOTION_RULES.toInterest,
        toDesire: DEFAULT_STAGE_PROMOTION_RULES.toDesire,
        toAction: DEFAULT_STAGE_PROMOTION_RULES.toAction,
        toDesireCumulative,
        couponClaimToDesire,
    };
}
/**
 * 「保存 / 導出ステージ + 事実アンカー」→「実効ステージ」の合成を 1 箇所に集約する（M-119）。
 *
 * `applyStageDecay(detail.stage, detail, evaluatedAt, resolveStageDecayRule(key, config))` と
 * 完全に同値。従来は配信抽出（`matchesSegment`）/ HOT 検知（`isTrackedDesire`）/ 顧客画面
 * （`resolveCustomerStage`）の 3 箇所に同じ合成がインラインで重複していた — KPI リプレイ
 * （`deriveUserStageSnapshot`）が 4 つ目になるので、**実効ステージの定義が配信判定と
 * ズレない**よう入口を 1 つにする。
 *
 * `detail` は `InterestStageDetail`（derive 出力）でも `{ stage: stored, ...interestStageMeta[key] }`
 * （users 保存値）でもよい — `applyStageDecay` は `lastTouchAt` / `couponUsableUntil` /
 * `cycleDueAt` だけを読み、余分なフィールドは無視する。
 */
export function resolveEffectiveStage(detail, evaluatedAt, key, config) {
    return applyStageDecay(detail.stage, detail, evaluatedAt, resolveStageDecayRule(key, config));
}
//# sourceMappingURL=promoEngine.js.map